import { FC, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Text } from 'ui'
import { BellIcon, GiftIcon, HeartIcon, CouponIcon, ShareIcon, NotificationsGiftIcon, SmallRedDot } from './icons'
// Note: HeartIcon matches what Context returns for 'like'.

import { NotificationData } from './types'
import { hideScrollbarStyles, FlexSpaceBetween, CheckBoxConatiner, StyledCheckBox } from './SharedStyledComponents'
import { useNotifications } from '../../../store/NotificationsContext'
import { useCoupons } from '../../../store/CouponsContext'

const NotificationsContainer = styled.div<{ isOpen: boolean; isMobileView?: boolean }>`
  display: flex;
  max-width: ${p => p.isOpen ? '450px' : 'fit-content'};
  min-width: ${p => p.isOpen ? '416px' : 'auto'};
  min-height: 100%;
  padding: ${p => p.isOpen ? '32px 16px 24px 16px' : '16px'};
  flex-direction: column;
  align-items: center;
  gap: 24px;
  background: #E7E7E7;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: fit-content;
  overflow-y: auto;
  max-height: 100%;
  ${hideScrollbarStyles}
  @media (max-width: 1081px) {
    min-width: unset;
    width: 100%;
    display: ${p => p.isMobileView ? 'flex' : 'none'};
    max-width: 100%;
    height: auto;
    padding: 16px;
  }
`
const Header = FlexSpaceBetween

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`

const NotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  align-self: stretch;
  overflow: hidden;
`

const NotificationsListContainer = styled.div`
  display: flex;
  flex-direction: column;
    align-self: stretch;
`
const NotificationItem = styled.div<{ isRead: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  padding: 16px 12px;
  background: ${p => p.isRead ? '#E7E7E7' : '#F9F9F9'};
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #8D8D8D;
  max-width: 380px;
  width: 380px;
  &:hover {
    background: #F0F0F0;
  }
  &:last-child {
    border-bottom: none;
  }
  @media (max-width: 1081px) {
    max-width: 100%;
    width: 100%;
  }
`
const NotificationItemContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const TitleAndTimeWrapper = styled(FlexSpaceBetween)`
  align-self: stretch;
  margin-bottom: 4px;
`

const IconWrapper = styled.div<{ bgColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${p => p.bgColor || '#E8F5E9'};
  aspect-ratio: 1/1;
`
const ArrowMenuOpenIcon = styled.div<{ isOpen: boolean; isMobileView?: boolean }>`
  padding: 8px;
  background: white;
  border-radius: 100px;
  margin-right: auto;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  &:hover {
    background: #F0F0F0;
  }
  @media (max-width: 1081px) {
    display: ${p => p.isMobileView ? 'none' : 'block'};
  }
`

const CheckboxLabel = styled.label`
  display: flex;
  height: 45px;
  padding: 12px 4px;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 10px;
  background: #FFF;
  cursor: pointer;
`
const EverythingLabel = styled.div`
  display: flex;
  height: 45px;
  padding: 16px 12px;
  align-items: center;
  gap: 10px;
  border-radius: 10px;
  background: #FFF;
  align-self: stretch;
  justify-content: space-between;
  cursor: pointer;
`

const RegularLabel = styled.div`
  display: flex;
  justify-content: flex-start;
  height: 45px;
  align-self: stretch;
  align-items: center;
`


const ShowPreviousNotifications = styled.button`
  display: flex;
  height: 44px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #B0B0B0;
  cursor: pointer;
  background: transparent;
  &:hover {
    background: #E7E7E7;
  }
`

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  display: ${p => p.isOpen ? 'flex' : 'none'};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
`

const DropdownItem = styled.div<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  background: ${p => p.isSelected ? '#F0F0F0' : 'white'};
  transition: background 0.2s;
  
  &:hover {
    background: #F5F5F5;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #E7E7E7;
  }
`

interface NotificationsProps {
  setIsConfirmModalOpen: (isConfirmModalOpen: boolean) => void
  setConfirmModalTitle: (confirmModalTitle: string) => void
  setConfirmModalSubtitle: (confirmModalSubtitle: string) => void
  setConfirmModalConfirmButtonText: (confirmModalConfirmButtonText: string) => void
  setConfirmModalCancelButtonText: (confirmModalCancelButtonText: string) => void
  setConfirmModalIsConfettiVisible: (visible: boolean) => void
  setConfirmModalIsGiftIconVisible: (visible: boolean) => void
  setConfirmModalConfirmButtonBackground: (bg: string) => void
  setConfirmModalConfirmButtonBorderColor: (color: string) => void
  setConfirmModalConfirmButtonTextColor: (color: string) => void
  setConfirmModalCancelButtonBackground: (bg: string) => void
  setConfirmModalCancelButtonBorderColor: (color: string) => void
  setConfirmModalCancelButtonTextColor: (color: string) => void
  setConfirmModalSource: (source: 'notifications' | 'blockMembers') => void
  isMobileView?: boolean
  onCouponClick?: (couponId: number) => void
}

const Notifications: FC<NotificationsProps> = ({
  setIsConfirmModalOpen,
  setConfirmModalTitle,
  setConfirmModalSubtitle,
  setConfirmModalConfirmButtonText,
  setConfirmModalCancelButtonText,
  setConfirmModalIsConfettiVisible,
  setConfirmModalIsGiftIconVisible,
  setConfirmModalConfirmButtonBackground,
  setConfirmModalConfirmButtonBorderColor,
  setConfirmModalConfirmButtonTextColor,
  setConfirmModalCancelButtonBackground,
  setConfirmModalCancelButtonBorderColor,
  setConfirmModalCancelButtonTextColor,
  setConfirmModalSource,
  isMobileView = false,
  onCouponClick
}) => {
  const [isOpen, setIsOpen] = useState(true)
  const [showMoreEarlier, setShowMoreEarlier] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState<string>('הכל')
  const [markAllRead, setMarkAllRead] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)

  const { notifications, markAsRead, markAllAsRead } = useNotifications()
  const { coupons } = useCoupons()

  const filterOptions = [
    { label: 'הכל', icon: null },
    { label: 'מערכת', icon: HeartIcon },
    { label: 'מתנות', icon: NotificationsGiftIcon },
    { label: 'הטבות', icon: CouponIcon },
    { label: 'שיתופים והגבות', icon: ShareIcon }
  ]

  const getFilteredNotifications = (notificationsList: NotificationData[]) => {
    if (selectedFilter === 'הכל') {
      return notificationsList
    }

    const selectedOption = filterOptions.find(opt => opt.label === selectedFilter)
    if (!selectedOption || !selectedOption.icon) {
      return notificationsList
    }

    // Filter by icon type matching. 
    // This compares the component functions. Ensure they match exactly what's returned by context.
    return notificationsList.filter(n => n.icon === selectedOption.icon)
  }

  const todayNotifications = getFilteredNotifications(notifications.filter(n => n.today))
  const earlierNotifications = getFilteredNotifications(notifications.filter(n => !n.today))
  const displayedEarlierNotifications = showMoreEarlier
    ? earlierNotifications
    : earlierNotifications.slice(0, 4)

  const handleToggleMenu = () => {
    setIsOpen(!isOpen)
  }

  const handleShowMore = () => {
    setShowMoreEarlier(true)
  }

  const handleShowLess = () => {
    setShowMoreEarlier(false)
  }

  const handleToggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const handleSelectFilter = (filter: string) => {
    setSelectedFilter(filter)
    setIsDropdownOpen(false)
  }

  const handleNotificationClick = (notification: NotificationData) => {
    // Mark as read
    if (!notification.read) {
      markAsRead(notification.id)
    }

    // Handle Coupon Navigation
    if (notification.couponId && onCouponClick) {
      const couponIdNum = Number(notification.couponId)
      // Check if coupon exists in loaded coupons
      const coupon = coupons.find(c => c.id === couponIdNum || (c as any).firestoreId === notification.couponId)

      if (coupon) {
        onCouponClick(coupon.id)
        return
      }
    }

    // Standard notification (like, share, etc.)
    // Just show details
    setConfirmModalTitle(notification.title)
    setConfirmModalSubtitle(notification.description)
    setConfirmModalConfirmButtonText('סמן כנקרא')
    setConfirmModalCancelButtonText('סגור')
    setConfirmModalIsConfettiVisible(false)
    setConfirmModalIsGiftIconVisible(false)

    setConfirmModalConfirmButtonBackground('#007EFF')
    setConfirmModalConfirmButtonBorderColor('#007EFF')
    setConfirmModalConfirmButtonTextColor('white')
    setConfirmModalCancelButtonBackground('#FFF')
    setConfirmModalCancelButtonBorderColor('#B0B0B0')
    setConfirmModalCancelButtonTextColor('#272727')

    setConfirmModalSource('notifications')
    setIsConfirmModalOpen(true)
  }



  const handleMarkAllRead = async () => {

      setMarkAllRead(!markAllRead)
    if (!markAllRead) {
      await markAllAsRead()
      // state will update via effect
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])



  return (
    <NotificationsContainer isOpen={isOpen} isMobileView={isMobileView}>
      <Header>
        {isOpen && (
          <HeaderTitle>
            {
              notifications.length > 0 &&
                <SmallRedDot />
            }
            <BellIcon fill="#272727" />
            <Text size="xl" weight="bold" textcolor="#272727">
              התראות
            </Text>
          </HeaderTitle>
        )}
        <ArrowMenuOpenIcon
          isOpen={isOpen}
          isMobileView={isMobileView}
          onClick={handleToggleMenu}
        >
          {/* Using SVG directly for arrow since imported icon was IMG in previous code but I prefer code consistency if possible. 
                 Previous code: src={isOpen ? ArrowMenuOpen : BellIcon}
                 Icons are imported components now.
             */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <path d="M15 18L9 12L15 6" stroke="#272727" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ArrowMenuOpenIcon>
      </Header>
      {isOpen && (
        <>
          <DropdownContainer ref={dropdownRef}>
            <EverythingLabel onClick={handleToggleDropdown}>
              <Text size="md" weight="bold" textcolor="#272727">{selectedFilter}</Text>
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none" style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>
                <path fillRule="evenodd" clipRule="evenodd" d="M5.33954 7.70938C5.63812 7.42228 6.11291 7.43159 6.4 7.73017L10.1094 11.6679L13.8188 7.73017C14.1058 7.43159 14.5806 7.42228 14.8792 7.70938C15.1778 7.99647 15.1871 8.47125 14.9 8.76983L10.65 13.2698C10.5086 13.4169 10.3134 13.5 10.1094 13.5C9.90536 13.5 9.71015 13.4169 9.56875 13.2698L5.31875 8.76983C5.03166 8.47125 5.04097 7.99647 5.33954 7.70938Z" fill="#272727" />
              </svg>
            </EverythingLabel>
            <DropdownMenu isOpen={isDropdownOpen}>
              {filterOptions.map((option) => (
                <DropdownItem
                  key={option.label}
                  isSelected={selectedFilter === option.label}
                  onClick={() => handleSelectFilter(option.label)}
                >
                  <Text size="md" weight={selectedFilter === option.label ? "bold" : "normal"} textcolor="#272727">
                    {option.label}
                  </Text>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </DropdownContainer>

          <CheckboxLabel onClick={handleMarkAllRead}>
            <CheckBoxConatiner>
              <StyledCheckBox type="checkbox" checked={markAllRead}  />
            </CheckBoxConatiner>
            <Text size="sm" weight="normal" textcolor="#272727">
              סימון כל ההתראות כ"נקראו"
            </Text>
          </CheckboxLabel>

          {todayNotifications.length > 0 && (
            <NotificationsListContainer>
              <RegularLabel>
                <Text size="md" weight="bold" textcolor="#272727">חדש</Text>
              </RegularLabel>
              <NotificationsList>
                {todayNotifications.map((notification) => {
                  const IconComponent = notification.icon;
                  return (
                    <NotificationItem
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      isRead={notification.read}
                    >
                      <IconWrapper bgColor={notification.iconBg}>
                        <IconComponent fill={notification.iconFill || '#272727'} />
                      </IconWrapper>
                      <NotificationItemContentWrapper>
                        <TitleAndTimeWrapper>
                          <Text size="md" weight="normal" textcolor="#272727">
                            {notification.title.length > 28
                              ? `${notification.title.slice(0, 28)}...`
                              : notification.title}
                          </Text>
                          <Text size="sm" weight="normal" textcolor="#272727" opacity={0.6}>
                            {notification.time}
                          </Text>
                        </TitleAndTimeWrapper>
                        <Text size="md" weight="normal" textcolor="#272727" opacity={0.6}>
                          {notification.description.length > 40
                            ? `${notification.description.slice(0, 40)}...`
                            : notification.description}
                        </Text>
                      </NotificationItemContentWrapper>
                    </NotificationItem>
                  )
                })}
              </NotificationsList>
            </NotificationsListContainer>
          )}
          {earlierNotifications.length > 0 && (
            <NotificationsListContainer>
              <RegularLabel>
                <Text size="md" weight="bold" textcolor="#272727">מוקדם יותר</Text>
              </RegularLabel>
              <NotificationsList>
                {displayedEarlierNotifications.map((notification) => {
                  const IconComponent = notification.icon;
                  return (
                    <NotificationItem
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      isRead={notification.read}
                    >
                      <IconWrapper bgColor={notification.iconBg}>
                        <IconComponent fill={notification.iconFill || '#272727'} />
                      </IconWrapper>
                      <NotificationItemContentWrapper>
                        <TitleAndTimeWrapper>
                          <Text size="md" weight="normal" textcolor="#272727">
                            {notification.title.length > 28
                              ? `${notification.title.slice(0, 28)}...`
                              : notification.title}
                          </Text>
                          <Text size="sm" weight="normal" textcolor="#272727" opacity={0.6}>
                            {notification.time}
                          </Text>
                        </TitleAndTimeWrapper>
                        <Text size="md" weight="normal" textcolor="#272727" opacity={0.6}>
                          {notification.description.length > 40
                            ? `${notification.description.slice(0, 40)}...`
                            : notification.description}
                        </Text>
                      </NotificationItemContentWrapper>
                    </NotificationItem>
                  )
                })}
              </NotificationsList>
            </NotificationsListContainer>
          )}
          {!showMoreEarlier && earlierNotifications.length > 4 && (
            <ShowPreviousNotifications onClick={handleShowMore}>
              <Text size="md" weight="medium" textcolor="#272727" align="center">הצגת התראות קודמות</Text>
            </ShowPreviousNotifications>
          )}
          {showMoreEarlier && earlierNotifications.length > 4 && (
            <ShowPreviousNotifications onClick={handleShowLess}>
              <Text size="md" weight="medium" textcolor="#272727" align="center">הצגת פחות התראות</Text>
            </ShowPreviousNotifications>
          )}
        </>
      )}
    </NotificationsContainer>
  )
}

export default Notifications

